import { motion } from "framer-motion";
import { Megaphone, Monitor, Database, ShieldCheck, Check, ArrowUpRight, Users } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const SERVICES_IMG = {
  marke: "/baustein-marke.jpg",
  patienten: "/baustein-patienten.jpg",
  personal: "/baustein-personal.jpg",
  systeme: "/baustein-systeme.jpg",
};

// object-position je Bild: Gesichter/Köpfe bleiben sichtbar, nichts wird abgeschnitten
const IMG_POS = {
  marke: "50% 42%",
  patienten: "50% 50%",
  personal: "50% 26%",
  systeme: "50% 55%",
};

const ICONS = { megaphone: Megaphone, monitor: Monitor, database: Database, shield: ShieldCheck, users: Users };

const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="leistungen" className="bg-dark py-20 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-16">
        <Reveal>
          <Eyebrow>{t.services.eyebrow}</Eyebrow>
        </Reveal>
        <div className="mt-4 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <Reveal delay={0.08}>
            <h2 className="max-w-2xl font-display text-4xl font-medium tracking-tight sm:text-5xl">{t.services.title}</h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="max-w-md text-base leading-relaxed text-inkmuted">{t.services.sub}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[s.icon] || Megaphone;
            return (
              <motion.article
                key={s.id}
                id={s.id}
                data-testid={`baustein-${s.id}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1, margin: "0px 0px 120px 0px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative flex scroll-mt-28 flex-col overflow-hidden rounded-[3px] border border-bone/10 bg-carddark transition-colors duration-300 hover:border-gold/40 ${i % 2 === 1 ? "md:translate-y-8" : ""}`}
              >
                <div className="relative h-56 overflow-hidden lg:h-64">
                  <img src={SERVICES_IMG[s.id]} alt={s.title} loading="lazy" style={{ objectPosition: IMG_POS[s.id] }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-carddark via-transparent to-transparent" aria-hidden="true" />
                  <span className="absolute right-4 top-4 font-mono text-xs text-bone/70">[{String(i + 1).padStart(2, "0")}]</span>
                </div>
                <div className="flex grow flex-col p-8 lg:p-10 lg:pt-6">
                <div className="flex items-start justify-between gap-6">
                  <span className="inline-flex rounded-full border border-gold/25 bg-gold/10 p-3 text-gold">
                    <Icon size={20} />
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-medium tracking-tight sm:text-3xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-inkmuted">{s.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.checks.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-bone/75">
                      <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                      {c}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid={`baustein-${s.id}-cta`}
                  onClick={() => scrollToId("kontakt")}
                  className="mt-8 inline-flex items-center gap-2 self-start border-b border-gold/40 pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:border-gold hover:text-bone"
                >
                  {t.services.more}
                  <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
